import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";

export const Navigation = ({ onBookingClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // In-page anchor links (scroll on the home page)
  const scrollLinks = [
    { href: "#about", label: "Über uns" },
    { href: "#services", label: "Behandlungen" },
    { href: "#gallery", label: "Galerie" },
    { href: "#reviews", label: "Bewertungen" },
    { href: "#contact", label: "Kontakt" },
  ];

  // Standalone pages (separate routes)
  const pageLinks = [
    { to: "/products", label: "Products" },
    { to: "/futura-contour", label: "Futura Contour®" },
    { to: "/coolite-bolt-laser", label: "Coolite Bolt Laser" },
    { to: "/laser-hautverjuengung", label: "Laser Hautverjüngung & Hautstraffung" },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  const textColor = isScrolled ? "text-[#1A1A1A]" : "text-white";

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? "glass-nav py-3" : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="flex items-center justify-between gap-6">
            {/* Logo */}
            <Link
              to="/"
              data-testid="nav-logo"
              className={`font-logo text-3xl md:text-4xl leading-none flex-shrink-0 transition-colors duration-300 ${textColor}`}
            >
              Urban Beauty
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center gap-4 2xl:gap-6">
              {scrollLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  data-testid={`nav-link-${link.href.slice(1)}`}
                  className={`text-[11px] tracking-wider uppercase font-medium whitespace-nowrap transition-all duration-300 hover:opacity-70 ${textColor}`}
                >
                  {link.label}
                </button>
              ))}
              {pageLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-page-${link.to.slice(1)}`}
                  className={`text-[11px] tracking-wider uppercase font-medium whitespace-nowrap transition-all duration-300 hover:opacity-70 ${textColor}`}
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={onBookingClick}
                data-testid="nav-booking-btn"
                className={`px-5 py-2.5 rounded-full text-[11px] tracking-wider uppercase font-medium whitespace-nowrap transition-all duration-300 ${
                  isScrolled
                    ? "bg-[#1A1A1A] text-white hover:bg-[#333]"
                    : "bg-white text-[#1A1A1A] hover:bg-white/90"
                }`}
              >
                Termin buchen
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
              className={`xl:hidden p-2 transition-colors duration-300 ${textColor}`}
              aria-label="Menü öffnen"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`mobile-menu-overlay flex flex-col items-center justify-center transition-all duration-500 ${
          isMobileMenuOpen
            ? "opacity-100 visible"
            : "opacity-0 invisible pointer-events-none"
        }`}
        data-testid="mobile-menu-overlay"
      >
        <button
          onClick={() => setIsMobileMenuOpen(false)}
          className="absolute top-6 right-6 p-2 text-[#1A1A1A]"
          aria-label="Menü schließen"
        >
          <X size={24} />
        </button>

        <div className="flex flex-col items-center gap-5 max-h-[85vh] overflow-y-auto py-8">
          {scrollLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollToSection(link.href)}
              data-testid={`mobile-nav-link-${link.href.slice(1)}`}
              className="font-playfair text-2xl text-[#1A1A1A] hover:text-[#C5A059] transition-colors duration-300 text-center"
            >
              {link.label}
            </button>
          ))}
          {pageLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid={`mobile-nav-page-${link.to.slice(1)}`}
              className="font-playfair text-2xl text-[#1A1A1A] hover:text-[#C5A059] transition-colors duration-300 text-center"
            >
              {link.label}
            </Link>
          ))}
          <button
            onClick={() => {
              setIsMobileMenuOpen(false);
              onBookingClick();
            }}
            data-testid="mobile-booking-btn"
            className="mt-4 btn-primary"
          >
            Termin buchen
          </button>
        </div>
      </div>
    </>
  );
};
